<template>
  <div class="article" prerender>
    <div class="article--theme-gray">
      <div class="article__header">
        <div class="article__header-reflection"></div>
        <router-link class="article__logotype" to="/">
          <SvgSymbol use="#effect-logotype"/>
        </router-link>
      </div>
      <div class="article__view">
        <router-view />
      </div>
      <div class="article__footer">
        <OutlinesButton text="回首頁" @click.native.prevent="onHomeClick"/>
        <OutlinesButton :text="nextTitle" @click.native.prevent="onNextClick"/>
      </div>
    </div>
    <div class="article--theme-white">
      <TheComments class="article__comments" :url="projectUrl"/>
      <TheFooter/>
    </div>
  </div>
</template>

<script>
import OutlinesButton from '@/components/OutlinesButton'
import TheComments from '@/components/TheComments'
import TheFooter from '@/components/TheFooter'
import articles from '@/data/articles'

export default {
  name: 'Article',
  components: {
    OutlinesButton,
    TheComments,
    TheFooter
  },
  inject: [ 'projectUrl' ],
  data () {
    return {
      articles
    }
  },
  computed: {
    nextId () {
      let paths = this.$route.path.slice('/')
      let id = Number(paths[paths.length - 1]) + 1
      return (id > this.articles.length) ? 1 : id
    },
    nextTitle () {
      return this.nextId === 1 ? '第一篇' : '下一篇'
    }
  },
  methods: {
    onHomeClick () {
      this.$router.push({ name: 'home' })
    },
    onNextClick () {
      this.$ga('newmedia.send', {
        'hitType': 'event',
        'eventCategory': 'Link',
        'eventAction': 'Click',
        'eventLabel': `Next-to-article-${this.nextId}`
      })
      this.$router.push({ name: `article-${this.nextId}` })
    }
  }
}
</script>

<style lang="scss">
.article {
  position: relative;
  background: #efefef;

  &__comments {
    margin-top: 0;
    padding-top: 30px;
    @include rwd($RWD_TABLET) {
      padding-top: 40px;
    }
  }

  &__header {
    position: relative;
    height: 175px;
    background-image: linear-gradient(to bottom, rgb(232, 166, 135), rgb(243, 209, 138));
    border-bottom: 5px solid #e8a687;
    @include rwd($RWD_TABLET_SMALL) {
    }
    @include rwd($RWD_TABLET) {
    }
    @include rwd($RWD_DESKTOP) {
      height: 177px;
      border-bottom: 6px solid #e8a687;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      height: 200px;
      border-bottom: 6px solid #e8a687;
    }

    $degree: -15deg;
    @keyframes light {
      0% {
        opacity: 0.1;
        transform: translateX(-12%) skewX($degree);
      }
      80% {
        opacity: 0.12;
        transform: translateX(40%) skewX($degree);
      }
      100% {
        opacity: 0.1;
        transform: translateX(12%) skewX($degree);
      }
    }

    &-reflection {
      $degree: -15deg;
      position: absolute;
      top: 0;
      left: 50%;
      width: 90px;
      margin-left: -60px;
      height: 100%;
      @include rwd($RWD_TABLET_SMALL) {
        width: 100px;
        margin-left: -65px;
      }
      @include rwd($RWD_DESKTOP) {
        left: 240px;
      }
      @include rwd($RWD_DESKTOP_WIDE) {
        left: 260px;
        width: 130px;
        margin-left: -70px;
      }

      &:before,
      &:after {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: white;
        opacity: 0.1;
      }

      &:before {
        transform: translateX(-50%) skewX($degree);
        animation: light 6s infinite alternate both;
      }

      &:after {
        transform: translateX(50%) skewX($degree);
        animation: light 6s infinite alternate-reverse both;
      }
    }
  }

  &__logotype {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 211px;
    height: 98px;
    transform: translate(-50%, -50%);
    cursor: pointer;
    @include rwd($RWD_TABLET_SMALL) {
    }
    @include rwd($RWD_TABLET) {
    }
    @include rwd($RWD_DESKTOP) {
      top: 48px;
      left: 130px;
      transform: none;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      left: 138px;
      width: 265px;
      height: 123px;
    }
  }

  &__view {
    @include use-layer(&);
    position: relative;
    max-width: 1160px;
    margin: 0 auto;
  }

  &__info {
    padding: 22px 12px 0 20px;
    @include rwd($RWD_TABLET_SMALL) {
      padding: 35px 35px 0 35px;
    }
    @include rwd($RWD_TABLET) {
      padding: 35px 47px 0 47px;
    }
    @include rwd($RWD_DESKTOP) {
      float: left;
      width: 45%;
      margin-top: 50px;
      padding: 0 0 0 45px;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      width: 43%;
      margin-top: 60px;
      padding: 0;
    }
  }

  &__title {
    display: block;
    color: #1a1a1a;
    font-size: 1.5em;
    font-weight: bold;
    line-height: 1.25em;
    letter-spacing: 0.08em;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.875em;
      line-height: 1.2em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 2.25em;
      line-height: 1.333em;
    }
    @include rwd($RWD_DESKTOP) {
      font-size: 1.875em;
      line-height: 1.2em;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      font-size: 2.25em;
      line-height: 1.11em;
    }
  }

  &__subtitle {
    margin-top: 10px;
    color: #dd6f6f;
    font-size: 1.125em;
    font-weight: bold;
    line-height: 1.333em;
    letter-spacing: 0.08em;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.375em;
      line-height: 1.363em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1.5em;
      line-height: 1.25em;
    }
    @include rwd($RWD_DESKTOP) {
    }
    @include rwd($RWD_DESKTOP_WIDE) {
    }
  }

  &__reporter {
    margin: 12px 2px;
    font-size: 0.875em;
    font-weight: bold;
    line-height: 1.256em;
    letter-spacing: 0.1em;
    text-align: justify;
    text-justify: inter-word;
    @include rwd($RWD_TABLET_SMALL) {
      margin: 18px 4px 15px 4px;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1em;
    }
    @include rwd($RWD_DESKTOP) {
      margin: 25px 4px 30px 4px;
      font-size: 0.875em;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      margin: 25px 4px 35px 4px;
    }
  }

  &__figure {
    display: block;
    max-width: 760px;
    margin: 0 auto;
    padding: 10px;
    @include rwd($RWD_TABLET_SMALL) {
      padding: 10px 0;
    }
    @include rwd($RWD_TABLET) {
      padding: 10px 45px;
    }
    @include rwd($RWD_DESKTOP) {
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      padding: 10px 0px;
    }

    &-photo {
      background: #e6e6e6 no-repeat top center / cover;
      @include use-fixed-ratio(4, 3);
    }

    &-caption {
      position: relative;
      padding: 16px 5px;
      font-size: 0.875em;
      font-weight: bold;
      line-height: 1.714em;
      letter-spacing: 0.1em;
      text-align: justify;
      text-justify: inter-word;
      @include rwd($RWD_TABLET_SMALL) {
        margin: 0 35px;
        font-size: 1em;
      }
      @include rwd($RWD_TABLET) {
        margin: 0;
      }
      @include rwd($RWD_DESKTOP) {
      }
      @include rwd($RWD_DESKTOP_WIDE) {
      }

      &:after {
        content: "";
        display: block;
        position: absolute;
        bottom: 0;
        left: 5px;
        width: 120px;
        height: 0;
        border-bottom: 1px solid #dd6f6f;
      }
    }
  }

  &__cover {
    position: relative;
    @include rwd($RWD_DESKTOP) {
      float: right;
      width: 55%;
      margin-top: -4%;
      padding: 10px 45px;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      width: 57%;
      margin-top: -10%;
      padding: 10px 10px 10px 40px;
    }
  }

  &__figure-in-article {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
  }

  &__paragraph-title {
    margin: 35px 20px 10px 15px;
    color: #dd7070;
    font-size: 1.25em;
    font-weight: bold;
    line-height: 1.2em;
    letter-spacing: 0.05em;
    @include rwd($RWD_TABLET_SMALL) {
      margin: 50px 30px 15px 30px;
      font-size: 1.5em;
      line-height: 1.25em;
      letter-spacing: 0.1em;
    }
    @include rwd($RWD_TABLET) {
      margin: 50px 50px 15px 50px;
    }
    @include rwd($RWD_DESKTOP) {}
    @include rwd($RWD_DESKTOP_WIDE) {
      margin: 50px 0 15px 0;
      font-size: 1.875em;
      line-height: 1.2em;
    }
  }

  &__paragraph-title + &__paragraph {
    margin: 10px 15px;
    @include rwd($RWD_TABLET_SMALL) {
      margin: 15px 35px;
    }
    @include rwd($RWD_TABLET) {
      margin: 15px 50px;
    }
    @include rwd($RWD_DESKTOP) {}
    @include rwd($RWD_DESKTOP_WIDE) {
      margin: 15px 0 15px 0;
    }
  }

  &__paragraph {
    margin: 20px 15px;
    font-size: 1em;
    font-weight: bold;
    line-height: 1.5em;
    letter-spacing: 0.1em;
    text-align: justify;
    text-justify: inter-word;
    @include rwd($RWD_TABLET_SMALL) {
      margin: 25px 35px;
      font-size: 1.125em;
      line-height: 1.667em;
    }
    @include rwd($RWD_TABLET) {
      margin: 35px 50px;
    }
    @include rwd($RWD_DESKTOP) {}
    @include rwd($RWD_DESKTOP_WIDE) {
      margin: 35px 0 35px 0;
    }

    &:first-of-type {
      @include rwd($RWD_DESKTOP) {
        clear: left;
      }
    }
  }

  &__footer {
    margin: 50px 0 0 0;
    text-align: center;
    @include rwd($RWD_TABLET_SMALL) {
      margin: 50px 0 10px 0;
    }
    @include rwd($RWD_TABLET) {
      margin: 60px 0 30px 0;
    }
    @include rwd($RWD_DESKTOP) {
      margin: 50px 0 30px 0;
    }
    @include rwd($RWD_DESKTOP_WIDE) {
      margin: 70px 0 20px 0;
    }

    .outlines-button {
      min-width: 130px;
      @include rwd($RWD_TABLET) {
        min-width: 160px;
      }
      @include rwd($RWD_DESKTOP) {
        min-width: 143px;
      }
      &__text {
        @include rwd($RWD_TABLET) {
          font-size: 1.125em;
          line-height: 1.667em;
          letter-spacing: 0.1em;
        }
        @include rwd($RWD_DESKTOP) {
          font-size: 1em;
          line-height: 1.875em;
          letter-spacing: 0.1em;
        }
      }

      & + .outlines-button {
        margin-left: 20px;
      }
    }
  }

  &__chart {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 20px 20px;
    @include rwd($RWD_TABLET_SMALL) {
      padding: 40px 35px;
    }

    @include rwd($RWD_TABLET) {
      padding: 40px 50px;
    }

    @include rwd($RWD_DESKTOP) {
      padding: 60px 70px;
    }
  }

  &__chart-caption {
    padding-bottom: 15px;
    font: normal 100%/150% "Montserrat", "Noto Sans TC", "Noto Serif TC", sans-serif;
    font-size: 0.9375em;
    text-align: center;

    @include rwd($RWD_TABLET) {
      font-size: 1em;
    }

    @include rwd($RWD_DESKTOP) {
      padding-bottom: 20px;
    }
  }

  &__chart-figure {
    position: relative;
    color: #1A1A1A;

    .svg-symbol {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__chart-annotation {
    margin: 10px 5px 0 5px;
    text-align: right;

    span {
      display: block;
      color: #7e7e7e;
      font-size: 0.75em;
      font-family: "Montserrat", "Noto Sans TC", "Noto Serif TC", sans-serif;
      letter-spacing: 0.05em;
      @include rwd($RWD_TABLET_SMALL) {
        display: inline-block;
      }

      & + span {
        &:before {
          @include rwd($RWD_TABLET_SMALL) {
            content: "/";
            margin: 0 0.5em;
          }
        }
      }
    }
  }

  &__chart-extended {
    position: relative;
    margin-top: 20px;
    text-align: center;
    @include rwd($RWD_TABLET_SMALL) {
      text-align: right;
    }

    &-link {
      display: inline-block;
      color: #7e7e7e;
      padding: 5px 10px;
      text-align: left;
      border: 1px solid #7e7e7e;
      border-radius: 2px;

      &:active {
        transform: translateY(1px);
      }

      .svg-symbol {
        display: inline-block;
        width: 22px;
        height: 22px;
        vertical-align: middle;
      }

      span {
        margin-left: 6px;
        font-size: 0.875em;
        font-weight: bold;
        letter-spacing: 0.05em;
        vertical-align: middle;
      }
    }
  }
}

.desktop {
  .article {
    &__chart-extended {
      &-link-dpp {
        &:hover {
          color: #149840;
          border-color: #149840;
        }
      }
      &-link-kmt {
        &:hover {
          color: #10347c;
          border-color: #10347c;
        }
      }
    }
  }
}
</style>
